// The guest account box's transport (OpenHouse::EnrollmentsController under
// /join, and its intent at /join/intent) and the lines its shared Open House
// screens take in account-only form (wording.md §5–§6, §12). Same transport
// rules as Open House: fetch with the CSRF header; a refusal carries the
// server's machine `reason`.
import { csrfToken } from "@/lib/orderActions"
import { CONFIRMATION as C, REGISTRATION as R } from "@/lib/openHouse/wording"
import { VR, fill } from "./wording"

async function call(path, method, body) {
  const res = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data?.reason || "Request failed")
    Object.assign(err, { status: res.status, reason: data?.reason, data })
    throw err
  }
  return data
}

// The stable continue address (ViewingRoom::ResumesController): signed out, it
// goes through today's sign-in and back; signed in, it resumes the kept action.
export const RESUME_PATH = "/join/resume"
export const continueSignedIn = () => window.location.assign(RESUME_PATH)
export const SUPPORT_HREF = "mailto:support@dibbly.com"

// The search parameters the server keeps in a return destination
// (ViewingRoom::ActionIntent::SEARCH_KEYS); any other one would refuse it.
const SEARCH_KEYS = [
  "q", "role", "tier", "availability", "minRating", "minReliability", "online", "noActive", "sort", "page", "niches",
  "languages",
]

// Where the guest is now, as a destination the server accepts: the profile is
// its default (blank), search keeps its own parameters only.
export function returnHere(location = window.location) {
  if (location.pathname !== "/find-freelancers") return ""
  const kept = new URLSearchParams()
  new URLSearchParams(location.search).forEach((value, key) => {
    if (SEARCH_KEYS.includes(key)) kept.append(key, value)
  })
  const query = kept.toString()
  return query ? `/find-freelancers?${query}` : "/find-freelancers"
}

// Kept clicks are sent one at a time, in click order, so the server keeps
// the latest even when an earlier request is slower.
let holding = Promise.resolve()
export function holdIntent({ action, slug, returnTo }) {
  const body = {
    intent_action: action, freelancer: slug, return_to: returnTo,
    from: new URLSearchParams(window.location.search).get("from") || undefined,
  }
  const sent = holding.catch(() => {}).then(() => call("/join/intent", "POST", body))
  holding = sent
  return sent
}

// This browser's waiting enrollment, if any (404 without one).
export const enrollmentWaiting = () => call("/join/account", "GET")

export const enrollAccount = ({ firstName, lastName, email, password, recaptchaToken }) =>
  call("/join/account", "POST", {
    open_house_first_name: firstName, open_house_last_name: lastName, open_house_account_email: email, password,
    agreement: true, recaptcha_token: recaptchaToken,
  })

// The account screen's lines for an action (AccountScreen `words`).
export function accountWords(action, penName) {
  const lines = VR.account[action]
  return {
    heading: fill(lines.title, { pen_name: penName }),
    body: fill(lines.body, { pen_name: penName }),
    submit: lines.button,
    submitting: VR.account.submitting,
    confirmationRequired: VR.account.confirmationRequired,
    billingLater: null,
    nameHelp: null,
    agreementRequired: VR.account.agreementRequired,
    validationSummary: VR.account.validationSummary,
    existingBody: VR.account.existingBody,
    failedBody: VR.account.failedBody,
  }
}

// The confirmation refusals an account-only waiting screen shows, by reason.
function refusal({ reason, data }) {
  if (reason === "too_many_attempts") {
    return fill(VR.error.confirmationLimited, { retry_after_seconds: data?.retry_after_seconds })
  }
  if (reason === "not_pending" || reason === "expired") return VR.confirmation.noPendingAccount
  if (reason === "account_conflict") return VR.confirmation.accountConflict
  return C.failed
}

// WaitingScreen's `flow` for an account-only enrollment.
export function enrollmentFlow(action, penName) {
  return {
    confirm: (code) => call("/join/confirmation", "POST", { confirmation_code: code }),
    resend: () => call("/join/confirmation/resend", "POST", {}),
    correct: (email) => call("/join/confirmation/email", "POST", { open_house_account_email: email }),
    signIn: continueSignedIn,
    heading: VR.confirmation.title,
    lead: fill(VR.confirmation.next[action] ?? "", { pen_name: penName }),
    submit: VR.confirmation.submit,
    deliveryFailed: VR.confirmation.deliveryFailed,
    codeLocked: VR.error.codeAttemptsExhausted,
    usedUp: VR.error.confirmationEmailLimit,
    refusal,
    supportHref: SUPPORT_HREF,
    supportLabel: VR.confirmation.contactSupport,
    // An enrollment past its lifetime ends in the deleted account's screen.
    accountOnly: true,
    already: { body: VR.confirmation.confirmedElsewhere, button: R.existingButton },
    correction: { unchanged: VR.confirmation.emailUnchanged, inUseBody: VR.confirmation.emailInUseBody },
  }
}
